import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { trpc } from "../trpc.js";

type Tone = "slow" | "active" | "eager";

const TONE_KEY = "companion:tempo-tone";
const AGE_MIN_KEY = "companion:age-min";
const AGE_MAX_KEY = "companion:age-max";

function isTone(v: string | null): v is Tone {
  return v === "slow" || v === "active" || v === "eager";
}

interface ToneMeta {
  key: Tone;
  label: string;
  rhythm: string;
  rhythmAccent: string;
}

const DEFAULT_TONE: ToneMeta = {
  key: "slow",
  label: "медленный",
  rhythm: "один / ",
  rhythmAccent: "неделя",
};

const TONES: ToneMeta[] = [
  DEFAULT_TONE,
  { key: "active", label: "активный", rhythm: "три / ", rhythmAccent: "неделя" },
  { key: "eager", label: "жажду знакомств", rhythm: "", rhythmAccent: "ежедневно" },
];

const AGE_MIN = 18;
const AGE_MAX = 60;

export function Pause(): JSX.Element {
  const navigate = useNavigate();
  const [tone, setTone] = useState<Tone>("slow");
  const [ageMin, setAgeMin] = useState(24);
  const [ageMax, setAgeMax] = useState(34);
  // Отдельные stringовые стейты для редактирования — иначе при стирании
  // value пустая строка немедленно превращается в 18/60, и юзер не может
  // поменять «24 → 30» (ввод третьей цифры упирается в clamp против ageMax).
  const [ageMinInput, setAgeMinInput] = useState("24");
  const [ageMaxInput, setAgeMaxInput] = useState("34");
  const [open, setOpen] = useState<"tone" | "age" | null>(null);

  // Читаем сохранённые настройки темпа при монтировании.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const savedTone = window.localStorage.getItem(TONE_KEY);
    if (isTone(savedTone)) setTone(savedTone);
    const minRaw = window.localStorage.getItem(AGE_MIN_KEY);
    const maxRaw = window.localStorage.getItem(AGE_MAX_KEY);
    if (minRaw) {
      const n = Number(minRaw);
      if (!Number.isNaN(n) && n >= AGE_MIN && n <= AGE_MAX) {
        setAgeMin(n);
        setAgeMinInput(String(n));
      }
    }
    if (maxRaw) {
      const n = Number(maxRaw);
      if (!Number.isNaN(n) && n >= AGE_MIN && n <= AGE_MAX) {
        setAgeMax(n);
        setAgeMaxInput(String(n));
      }
    }
  }, []);

  // Сохраняем изменения.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(TONE_KEY, tone);
      window.localStorage.setItem(AGE_MIN_KEY, String(ageMin));
      window.localStorage.setItem(AGE_MAX_KEY, String(ageMax));
    } catch {
      /* privacy mode */
    }
  }, [tone, ageMin, ageMax]);

  const currentTone = TONES.find((t) => t.key === tone) ?? DEFAULT_TONE;

  const toggleOpen = (k: "tone" | "age") => {
    setOpen((cur) => (cur === k ? null : k));
  };

  const setToneAndClose = (k: Tone) => {
    setTone(k);
    setOpen(null);
  };

  // Пока юзер печатает — разрешаем любые 0–2 цифры (включая пустую строку).
  // Clamp и пересохранение только по blur — иначе посередине ввода значения
  // скачут и финальная цифра в лучшем случае обрезается.
  const onAgeInput = (which: "min" | "max", raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 2);
    if (which === "min") setAgeMinInput(digits);
    else setAgeMaxInput(digits);
  };

  const commitAge = (which: "min" | "max") => {
    if (which === "min") {
      const n = Number(ageMinInput);
      const base = !ageMinInput || Number.isNaN(n) ? ageMin : n;
      const clamped = Math.min(Math.max(base, AGE_MIN), ageMax);
      setAgeMin(clamped);
      setAgeMinInput(String(clamped));
    } else {
      const n = Number(ageMaxInput);
      const base = !ageMaxInput || Number.isNaN(n) ? ageMax : n;
      const clamped = Math.max(Math.min(base, AGE_MAX), ageMin);
      setAgeMax(clamped);
      setAgeMaxInput(String(clamped));
    }
  };

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="N°I · 2026" />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[28px] pb-[28px]">
        <button
          type="button"
          onClick={() => {
            navigate("/home");
          }}
          className="self-start editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← в редакцию
        </button>

        <h1 className="mt-[24px] font-display font-light leading-[1] tracking-[-0.028em] text-ink text-[44px]">
          Ваша <em className="italic text-velvet">редакция.</em>
        </h1>

        <div className="mt-[12px] editorial-meta text-ink/55">бета · бесплатно</div>

        <ul className="mt-[30px]">
          {/* Тон — выбирается, задаёт Ритм */}
          <li className="border-t border-b border-ink/12">
            <button
              type="button"
              onClick={() => {
                toggleOpen("tone");
              }}
              aria-expanded={open === "tone"}
              className="flex w-full items-baseline justify-between gap-[14px] py-[16px] text-left"
            >
              <span className="font-display font-light text-[18px] tracking-[-0.01em] text-ink">
                Тон
              </span>
              <span className="flex items-center gap-[10px]">
                <span className="editorial-meta text-ink/65">{currentTone.label}</span>
                <span className="editorial-meta text-accent">
                  {open === "tone" ? "готово" : "изменить"}
                </span>
              </span>
            </button>
            {open === "tone" && (
              <div className="pb-[16px] pt-[4px] space-y-[4px]">
                {TONES.map((t) => {
                  const isActive = t.key === tone;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setToneAndClose(t.key);
                      }}
                      className={`flex w-full items-baseline justify-between gap-[14px] border-l-2 pl-[14px] py-[8px] text-left transition-colors ${
                        isActive ? "border-accent" : "border-transparent hover:border-ink/20"
                      }`}
                    >
                      <span
                        className={`font-display font-light text-[17px] ${
                          isActive ? "italic text-velvet" : "text-ink/80"
                        }`}
                      >
                        {t.label}
                      </span>
                      <span className="editorial-meta text-ink/45">
                        {t.rhythm}
                        {t.rhythmAccent}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </li>

          {/* Ритм — производное от тона */}
          <li className="flex items-baseline justify-between gap-[14px] border-b border-ink/12 py-[16px]">
            <span className="font-display font-light text-[18px] tracking-[-0.01em] text-ink">
              Ритм
            </span>
            <span className="editorial-meta text-ink/65">
              {currentTone.rhythm}
              <em className="not-italic text-accent">{currentTone.rhythmAccent}</em>
            </span>
          </li>

          {/* Город — только Москва в бете */}
          <li className="flex items-baseline justify-between gap-[14px] border-b border-ink/12 py-[16px]">
            <span className="font-display font-light text-[18px] tracking-[-0.01em] text-ink">
              Город
            </span>
            <span className="editorial-meta text-ink/65">Москва</span>
          </li>

          {/* Возраст — редактируемый диапазон */}
          <li className="border-b border-ink/12">
            <button
              type="button"
              onClick={() => {
                toggleOpen("age");
              }}
              aria-expanded={open === "age"}
              className="flex w-full items-baseline justify-between gap-[14px] py-[16px] text-left"
            >
              <span className="font-display font-light text-[18px] tracking-[-0.01em] text-ink">
                Возраст
              </span>
              <span className="flex items-center gap-[10px]">
                <span className="editorial-meta text-ink/65">
                  {ageMin} — {ageMax}
                </span>
                <span className="editorial-meta text-accent">
                  {open === "age" ? "готово" : "изменить"}
                </span>
              </span>
            </button>
            {open === "age" && (
              <div className="pb-[20px] pt-[4px]">
                <div className="flex items-end gap-[18px]">
                  <label className="flex-1 block">
                    <span className="editorial-meta text-ink/55">от</span>
                    <input
                      value={ageMinInput}
                      onChange={(e) => {
                        onAgeInput("min", e.target.value);
                      }}
                      onBlur={() => {
                        commitAge("min");
                      }}
                      inputMode="numeric"
                      aria-label="возраст от"
                      className="mt-[4px] block w-full bg-transparent font-display font-light text-[28px] tracking-[-0.015em] text-ink outline-none border-b border-ink/20 focus:border-accent pb-[4px] transition-colors"
                    />
                  </label>
                  <span
                    aria-hidden
                    className="pb-[10px] font-display italic font-light text-[18px] text-ink/40"
                  >
                    —
                  </span>
                  <label className="flex-1 block">
                    <span className="editorial-meta text-ink/55">до</span>
                    <input
                      value={ageMaxInput}
                      onChange={(e) => {
                        onAgeInput("max", e.target.value);
                      }}
                      onBlur={() => {
                        commitAge("max");
                      }}
                      inputMode="numeric"
                      aria-label="возраст до"
                      className="mt-[4px] block w-full bg-transparent font-display font-light text-[28px] tracking-[-0.015em] text-ink outline-none border-b border-ink/20 focus:border-accent pb-[4px] transition-colors"
                    />
                  </label>
                </div>
                <p className="mt-[12px] font-display italic font-light text-[12px] leading-[1.45] text-ink/45">
                  от {AGE_MIN} до {AGE_MAX} лет — редакция не подбирает вне этого диапазона.
                </p>
              </div>
            )}
          </li>
        </ul>

        <p className="mt-[36px] text-center font-display italic font-light text-[15px] leading-[1.5] text-ink/60">
          «Сделать паузу, чтобы вернуться
          <br />к тому, что ещё впереди».
        </p>

        <GdprBlock />
      </div>
    </Screen>
  );
}

/**
 * GDPR-блок — две ссылки внизу редакции:
 *   1. «скачать мои данные» → trpc.me.exportData → blob JSON скачивается
 *   2. «удалить аккаунт»    → trpc.me.requestDeletion → ставит 7-дневный таймер,
 *      показывает статус + ссылку «отменить».
 */
function GdprBlock(): JSX.Element {
  const meQ = trpc.me.get.useQuery(undefined, { staleTime: 30_000 });
  const utils = trpc.useUtils();
  const exportQ = trpc.me.exportData.useQuery(undefined, { enabled: false, retry: false });
  const delMut = trpc.me.requestDeletion.useMutation({
    onSuccess: () => {
      void utils.me.get.invalidate();
    },
  });
  const cancelMut = trpc.me.cancelDeletion.useMutation({
    onSuccess: () => {
      void utils.me.get.invalidate();
    },
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const deletionRequestedAt = meQ.data?.user.deletionRequestedAt ?? null;
  const pendingDelete = deletionRequestedAt != null;

  const onExport = async (): Promise<void> => {
    const res = await exportQ.refetch();
    if (!res.data) return;
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "companion-export.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-[48px]">
      <div className="flex items-center gap-[12px] editorial-meta text-ink/45">
        <span className="h-px flex-1 bg-ink/10" />
        <span>данные</span>
        <span className="h-px flex-1 bg-ink/10" />
      </div>

      <ul className="mt-[14px]">
        <li className="border-t border-ink/10 py-[14px]">
          <button
            type="button"
            onClick={() => {
              void onExport();
            }}
            disabled={exportQ.isFetching}
            className="flex w-full items-baseline justify-between gap-[14px] text-left disabled:opacity-50"
          >
            <span className="font-display font-light text-[16px] text-ink/80">
              скачать мои данные
            </span>
            <span className="editorial-meta text-accent">
              {exportQ.isFetching ? "собираем…" : "json"}
            </span>
          </button>
        </li>

        <li className="border-t border-b border-ink/10 py-[14px]">
          {pendingDelete ? (
            <div className="flex items-baseline justify-between gap-[14px]">
              <span className="font-display font-light text-[16px] text-ink/80">
                удаление запланировано
              </span>
              <button
                type="button"
                disabled={cancelMut.isPending}
                onClick={() => {
                  cancelMut.mutate();
                }}
                className="editorial-meta text-accent disabled:opacity-50"
              >
                {cancelMut.isPending ? "отменяем…" : "отменить"}
              </button>
            </div>
          ) : confirmDelete ? (
            <div className="flex items-baseline justify-between gap-[14px]">
              <span className="font-display italic font-light text-[15px] text-ink/70">
                точно удалить?
              </span>
              <div className="flex items-center gap-[12px]">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDelete(false);
                  }}
                  className="editorial-meta text-ink/55"
                >
                  нет
                </button>
                <button
                  type="button"
                  disabled={delMut.isPending}
                  onClick={() => {
                    delMut.mutate();
                    setConfirmDelete(false);
                  }}
                  className="editorial-meta text-accent disabled:opacity-50"
                >
                  {delMut.isPending ? "ок…" : "да, удалить"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setConfirmDelete(true);
              }}
              className="flex w-full items-baseline justify-between gap-[14px] text-left"
            >
              <span className="font-display font-light text-[16px] text-ink/80">
                удалить аккаунт
              </span>
              <span className="editorial-meta text-ink/40">7 дней на отмену</span>
            </button>
          )}
        </li>
      </ul>
    </div>
  );
}
